import { ALL_FEATURES, getFeatureDescription } from "../constants/features"
import { MEMBER_SIZE_BUCKETS } from "../constants/memberSize"
import { readableNumber } from "./text"
import { topicIsoDate } from "./topics"

function platformSlugs(community) {
  return (community.platform || "independent")
    .split(",")
    .map(slug => slug.trim())
    .filter(Boolean)
}

function median(values) {
  if (!values.length) return null
  const sorted = [...values].sort((a, b) => a - b)
  const mid = Math.floor(sorted.length / 2)
  return sorted.length % 2 === 1
    ? sorted[mid]
    : (sorted[mid - 1] + sorted[mid]) / 2
}

function percent(part, total) {
  if (!total) return 0
  return Math.round((part / total) * 100)
}

function isRestrictedAccess(community) {
  return Boolean(community.apply || community.paid)
}

function getHighestRatedCommunities(communities, limit = 5) {
  return communities
    .filter(community => {
      const reviewCount = parseInt(community.reviewCount, 10)
      const reviewAvg = parseFloat(community.reviewAvg, 10)
      return (
        reviewCount > 0 &&
        !Number.isNaN(reviewCount) &&
        !Number.isNaN(reviewAvg)
      )
    })
    .sort((a, b) => {
      const avgDiff = parseFloat(b.reviewAvg, 10) - parseFloat(a.reviewAvg, 10)
      if (avgDiff !== 0) return avgDiff > 0 ? 1 : -1
      const countDiff =
        parseInt(b.reviewCount, 10) - parseInt(a.reviewCount, 10)
      return countDiff > 0 ? 1 : countDiff < 0 ? -1 : 0
    })
    .slice(0, limit)
}

function buildReviewBreakdown(reviews, communities) {
  const counts = { 1: 0, 2: 0, 3: 0, 4: 0, 5: 0 }

  ;(reviews || []).forEach(review => {
    const star = parseInt(review.overall, 10)
    if (star >= 1 && star <= 5) {
      counts[star] += 1
    }
  })

  const totalReviews = Object.values(counts).reduce(
    (sum, count) => sum + count,
    0
  )
  const reviewedCommunityCount = communities.filter(community => {
    const reviewCount = parseInt(community.reviewCount, 10)
    return reviewCount > 0 && !Number.isNaN(reviewCount)
  }).length

  const items = [5, 4, 3, 2, 1].map(star => ({
    key: String(star),
    slug: String(star),
    name: `${star} star${star === 1 ? "" : "s"}`,
    star,
    count: counts[star],
    percent: percent(counts[star], totalReviews),
  }))

  const averageRating = totalReviews
    ? (reviews || []).reduce(
        (sum, review) => sum + parseInt(review.overall, 10),
        0
      ) / totalReviews
    : null

  return {
    items,
    totalReviews,
    reviewedCommunityCount,
    unreviewedCommunityCount: communities.length - reviewedCommunityCount,
    averageRating,
  }
}

function computeTopicStats({
  topic,
  communities,
  platforms,
  features,
  reviews,
}) {
  const total = communities.length
  const platformBySlug = {}
  ;(platforms || []).forEach(platform => {
    platformBySlug[platform.slug] = platform
  })

  const isListedPlatform = slug =>
    slug === "independent" || Boolean(platformBySlug[slug])

  const platformCounts = {}
  communities.forEach(community => {
    platformSlugs(community).forEach(slug => {
      if (!isListedPlatform(slug)) return
      platformCounts[slug] = (platformCounts[slug] || 0) + 1
    })
  })

  const platformBreakdown = Object.keys(platformCounts)
    .map(slug => ({
      slug,
      name: (platformBySlug[slug] && platformBySlug[slug].name) || slug,
      logoSmall: platformBySlug[slug] && platformBySlug[slug].logoSmall,
      count: platformCounts[slug],
      percent: percent(platformCounts[slug], total),
    }))
    .filter(item => item.count > 0 && isListedPlatform(item.slug))
    .sort((a, b) => (a.count < b.count ? 1 : b.count < a.count ? -1 : 0))

  const featureStats = (
    features || ALL_FEATURES.map(slug => ({ slug, name: slug }))
  )
    .map(feature => {
      const count = communities.filter(c => c[feature.slug]).length
      return {
        slug: feature.slug,
        name: feature.name,
        description: getFeatureDescription(feature.slug, feature.name),
        count,
        percent: percent(count, total),
      }
    })
    .filter(feature => feature.count > 0)
    .sort((a, b) => (a.count < b.count ? 1 : b.count < a.count ? -1 : 0))

  const COMMON_FEATURE_THRESHOLD_PERCENT = 15
  const commonFeatures = featureStats.filter(
    feature => feature.percent >= COMMON_FEATURE_THRESHOLD_PERCENT
  )
  const nicheFeatures = featureStats.filter(
    feature => feature.percent < COMMON_FEATURE_THRESHOLD_PERCENT
  )

  const memberCounts = communities
    .map(c => c.members)
    .filter(members => members > 0)

  const averageMembers = memberCounts.length
    ? memberCounts.reduce((sum, value) => sum + value, 0) / memberCounts.length
    : null
  const medianMembers = median(memberCounts)

  const memberSizeDistribution = MEMBER_SIZE_BUCKETS.map(bucket => ({
    key: bucket.name,
    label: bucket.name,
    description: bucket.label,
    count: communities.filter(c => c.memberSize === bucket.name).length,
    percent: percent(
      communities.filter(c => c.memberSize === bucket.name).length,
      total
    ),
  }))

  const unknownMemberCount = communities.filter(c => c.memberSize === "Unknown")
    .length

  const restrictedCount = communities.filter(isRestrictedAccess).length
  const freeCount = total - restrictedCount

  const accessBreakdown = [
    {
      key: "free",
      label: "Free to join",
      count: freeCount,
      percent: percent(freeCount, total),
    },
    {
      key: "restricted",
      label: "Paid or apply-to-join",
      count: restrictedCount,
      percent: percent(restrictedCount, total),
    },
  ]

  const foundedTimeline = buildFoundedYearTimeline(communities)
  const addedTimeline = buildAddedToHiveTimeline(communities)
  const reviewBreakdown = buildReviewBreakdown(reviews, communities)
  const highestRatedCommunities = getHighestRatedCommunities(communities)
  const trafficStats = buildTrafficStats(communities, topic)
  const platformSupplyDemand = buildPlatformSupplyDemand(
    communities,
    platformBreakdown
  )
  const formatMix = buildFormatMix(communities, platformBreakdown)
  const highRatedFeatures = buildHighRatedFeatureInsights(
    communities,
    features,
    featureStats
  )
  const userClickInsights = buildUserClickInsights({
    communities,
    platformBreakdown,
    features,
    skipPlatformChart: true,
  })

  return {
    total,
    platformBreakdown,
    platformCount: platformBreakdown.length,
    commonFeatures,
    nicheFeatures,
    rareFeatures: nicheFeatures,
    memberSizeDistribution,
    unknownMemberCount,
    averageMembers,
    medianMembers,
    accessBreakdown,
    foundedTimeline,
    addedTimeline,
    reviewBreakdown,
    highestRatedCommunities,
    trafficStats,
    platformSupplyDemand,
    formatMix,
    highRatedFeatures,
    userClickInsights,
  }
}

function parseEstablishedYear(community) {
  const year = parseInt(community.established, 10)
  const maxYear = new Date().getFullYear() + 1
  if (
    community.established == null ||
    community.established === "" ||
    Number.isNaN(year) ||
    year < 1900 ||
    year > maxYear
  ) {
    return null
  }
  return year
}

function parseAddedMonthKey(community) {
  const iso = topicIsoDate(community.added)
  if (!iso) return null
  return iso.slice(0, 7)
}

function formatMonthLabel(monthKey) {
  const [year, month] = monthKey.split("-")
  const date = new Date(Number(year), Number(month) - 1, 1)
  return date.toLocaleDateString("en-US", { month: "short", year: "2-digit" })
}

function buildFoundedYearTimeline(communities) {
  let unknownCount = 0
  const counts = {}

  communities.forEach(community => {
    const year = parseEstablishedYear(community)
    if (!year) {
      unknownCount += 1
      return
    }
    counts[year] = (counts[year] || 0) + 1
  })

  const knownTotal = communities.length - unknownCount
  const items = Object.keys(counts)
    .sort((a, b) => Number(a) - Number(b))
    .map(year => ({
      key: year,
      label: year,
      count: counts[year],
      percent: percent(counts[year], knownTotal),
    }))

  return { items, unknownCount, knownTotal, yearCount: items.length }
}

function buildAddedToHiveTimeline(communities) {
  let unknownCount = 0
  const counts = {}

  communities.forEach(community => {
    const monthKey = parseAddedMonthKey(community)
    if (!monthKey) {
      unknownCount += 1
      return
    }
    counts[monthKey] = (counts[monthKey] || 0) + 1
  })

  const knownTotal = communities.length - unknownCount
  const monthKeys = Object.keys(counts).sort()
  const recentKeys = monthKeys.length > 24 ? monthKeys.slice(-24) : monthKeys

  const monthlyItems = recentKeys.map(monthKey => ({
    key: monthKey,
    label: formatMonthLabel(monthKey),
    count: counts[monthKey],
    percent: percent(counts[monthKey], knownTotal),
  }))

  const yearCounts = {}
  monthKeys.forEach(monthKey => {
    const year = monthKey.slice(0, 4)
    yearCounts[year] = (yearCounts[year] || 0) + counts[monthKey]
  })

  const yearItems = Object.keys(yearCounts)
    .sort()
    .map(year => ({
      key: year,
      label: year,
      count: yearCounts[year],
      percent: percent(yearCounts[year], knownTotal),
    }))

  const useMonthly = monthlyItems.length >= 6
  const items = useMonthly ? monthlyItems : yearItems
  const peakItem = items.reduce(
    (best, item) => (item.count > (best?.count || 0) ? item : best),
    null
  )

  return {
    items,
    monthlyItems,
    yearItems,
    useMonthly,
    peakLabel: peakItem?.label || null,
    peakCount: peakItem?.count || 0,
    unknownCount,
    knownTotal,
  }
}

function buildHighRatedFeatureInsights(communities, features, featureStats) {
  const rated = communities.filter(community => {
    const avg = parseFloat(community.reviewAvg, 10)
    const count = parseInt(community.reviewCount, 10)
    return count > 0 && !Number.isNaN(avg) && !Number.isNaN(count) && avg >= 4
  })

  if (rated.length < 3) {
    return { available: false, items: [], sampleSize: rated.length }
  }

  const categoryBySlug = {}
  ;(featureStats || []).forEach(feature => {
    categoryBySlug[feature.slug] = feature.percent
  })

  const featureList = features?.length
    ? features
    : ALL_FEATURES.map(slug => ({ slug, name: slug }))

  const items = featureList
    .map(feature => {
      const count = rated.filter(community => community[feature.slug]).length
      const ratedPercent = percent(count, rated.length)
      const categoryPercent = categoryBySlug[feature.slug] ?? null
      return {
        slug: feature.slug,
        name: feature.name,
        count,
        percent: ratedPercent,
        categoryPercent,
        overIndex:
          categoryPercent != null && ratedPercent - categoryPercent >= 10,
      }
    })
    .filter(item => item.count > 0)
    .sort((a, b) => (a.count < b.count ? 1 : b.count < a.count ? -1 : 0))
    .slice(0, 5)

  return {
    available: items.length >= 2,
    items,
    sampleSize: rated.length,
  }
}

function toTrafficNumber(value) {
  if (value == null || value === "") return 0
  const parsed = Number(String(value).replace(/,/g, ""))
  return Number.isFinite(parsed) ? parsed : 0
}

function buildCommunityTrafficRankings(communities, field, limit = 8) {
  const ranked = communities
    .map(community => ({
      slug: community.slug,
      name: community.name,
      count: toTrafficNumber(community[field]),
    }))
    .filter(community => community.count > 0)
    .sort((a, b) => (a.count < b.count ? 1 : b.count < a.count ? -1 : 0))

  const total = ranked.reduce((sum, community) => sum + community.count, 0)

  return {
    total,
    withDataCount: ranked.length,
    withoutDataCount: communities.length - ranked.length,
    items: ranked.slice(0, limit).map(community => ({
      key: community.slug,
      slug: community.slug,
      communitySlug: community.slug,
      name: community.name,
      count: community.count,
      percent: percent(community.count, total),
    })),
  }
}

function buildTrafficStats(communities, topic) {
  return {
    topicViews: toTrafficNumber(topic?.viewsWithCommunities ?? topic?.views),
    topicVisitors: toTrafficNumber(topic?.visitors),
    topicRedirects: toTrafficNumber(topic?.redirects),
    topByViews: buildCommunityTrafficRankings(communities, "views", 5),
    topByRedirects: buildCommunityTrafficRankings(communities, "redirects", 5),
  }
}

function buildClickChart(items, totalClicks, title, id, limit = 5) {
  const ranked = items
    .filter(item => item.count > 0)
    .sort((a, b) => (a.count < b.count ? 1 : b.count < a.count ? -1 : 0))

  if (ranked.length < 2) return null

  return {
    id,
    title,
    items: ranked.slice(0, limit).map(item => ({
      key: item.key || item.slug || item.name,
      slug: item.slug || item.key,
      name: item.name,
      count: item.count,
      percent: percent(item.count, totalClicks),
    })),
  }
}

function buildUserClickInsights({
  communities,
  platformBreakdown,
  features,
  skipPlatformChart,
}) {
  const totalClicks = communities.reduce(
    (sum, community) => sum + toTrafficNumber(community.redirects),
    0
  )

  if (!totalClicks) {
    return {
      available: false,
      totalClicks: 0,
      charts: [],
      topCommunities: [],
    }
  }

  const charts = []

  if (!skipPlatformChart && platformBreakdown.length) {
    const platformBySlug = {}
    platformBreakdown.forEach(platform => {
      platformBySlug[platform.slug] = platform
    })
    const platformClicks = {}
    communities.forEach(community => {
      const clicks = toTrafficNumber(community.redirects)
      if (!clicks) return
      const slug = platformSlugs(community)[0] || "independent"
      if (!platformBySlug[slug] && slug !== "independent") return
      platformClicks[slug] = platformClicks[slug] || {
        key: slug,
        slug,
        name: platformBySlug[slug]?.name || slug,
        count: 0,
      }
      platformClicks[slug].count += clicks
    })
    const platformChart = buildClickChart(
      Object.values(platformClicks),
      totalClicks,
      "Most-clicked platforms",
      "platform"
    )
    if (platformChart) charts.push(platformChart)
  }

  const formatClicks = {}
  communities.forEach(community => {
    const clicks = toTrafficNumber(community.redirects)
    if (!clicks) return
    const slug = platformSlugs(community)[0] || "independent"
    const formatKey = PLATFORM_FORMAT_MAP[slug] || "other"
    formatClicks[formatKey] = formatClicks[formatKey] || {
      key: formatKey,
      name: FORMAT_LABELS[formatKey] || "Other",
      count: 0,
    }
    formatClicks[formatKey].count += clicks
  })
  const formatChart = buildClickChart(
    Object.values(formatClicks),
    totalClicks,
    "Most-clicked formats",
    "format"
  )
  if (formatChart) charts.push(formatChart)

  const featureList = features?.length
    ? features
    : ALL_FEATURES.map(slug => ({ slug, name: slug }))
  const featureClicks = featureList.map(feature => ({
    key: feature.slug,
    slug: feature.slug,
    name: feature.name,
    count: communities.reduce(
      (sum, community) =>
        sum +
        (community[feature.slug] ? toTrafficNumber(community.redirects) : 0),
      0
    ),
  }))
  const featureChart = buildClickChart(
    featureClicks,
    totalClicks,
    "Most-clicked features",
    "features",
    6
  )
  if (featureChart) charts.push(featureChart)

  const sizeClicks = {}
  communities.forEach(community => {
    const clicks = toTrafficNumber(community.redirects)
    if (!clicks) return
    const key = community.memberSize || "Unknown"
    sizeClicks[key] = sizeClicks[key] || { key, name: key, count: 0 }
    sizeClicks[key].count += clicks
  })
  const sizeChart = buildClickChart(
    Object.values(sizeClicks).filter(item => item.key !== "Unknown"),
    totalClicks,
    "Most-clicked size ranges",
    "size"
  )
  if (sizeChart) charts.push(sizeChart)

  const accessClicks = [
    { key: "free", name: "Free to join", count: 0 },
    { key: "restricted", name: "Paid or apply-to-join", count: 0 },
  ]
  communities.forEach(community => {
    const clicks = toTrafficNumber(community.redirects)
    if (!clicks) return
    const bucket = isRestrictedAccess(community)
      ? accessClicks[1]
      : accessClicks[0]
    bucket.count += clicks
  })
  const accessChart = buildClickChart(
    accessClicks,
    totalClicks,
    "Clicks by access model",
    "access"
  )
  if (accessChart) charts.push(accessChart)

  const ratedWithClicks = communities.filter(community => {
    const reviewCount = parseInt(community.reviewCount, 10)
    return (
      reviewCount > 0 &&
      !Number.isNaN(reviewCount) &&
      toTrafficNumber(community.redirects) > 0
    )
  })
  if (ratedWithClicks.length >= 5) {
    const ratingBuckets = [
      { key: "4.5+", name: "4.5+ stars", count: 0 },
      { key: "4-4.4", name: "4.0–4.4 stars", count: 0 },
      { key: "3-3.9", name: "3.0–3.9 stars", count: 0 },
      { key: "below-3", name: "Below 3 stars", count: 0 },
    ]
    ratedWithClicks.forEach(community => {
      const avg = parseFloat(community.reviewAvg, 10)
      if (Number.isNaN(avg)) return
      const clicks = toTrafficNumber(community.redirects)
      let bucket = ratingBuckets[3]
      if (avg >= 4.5) bucket = ratingBuckets[0]
      else if (avg >= 4) bucket = ratingBuckets[1]
      else if (avg >= 3) bucket = ratingBuckets[2]
      bucket.count += clicks
    })
    const ratingChart = buildClickChart(
      ratingBuckets,
      totalClicks,
      "Clicks by rating",
      "rating"
    )
    if (ratingChart) charts.push(ratingChart)
  }

  const priority = [
    "format",
    "features",
    "size",
    "access",
    "platform",
    "rating",
  ]
  const selectedCharts = priority
    .map(id => charts.find(chart => chart.id === id))
    .filter(Boolean)
    .slice(0, 3)

  const topCommunities = buildCommunityTrafficRankings(
    communities,
    "redirects",
    3
  ).items

  return {
    available: selectedCharts.length > 0 || topCommunities.length > 0,
    totalClicks,
    charts: selectedCharts,
    topCommunities,
  }
}

const PLATFORM_FORMAT_MAP = {
  reddit: "feed",
  facebook: "feed",
  quora: "feed",
  discourse: "feed",
  independent: "feed",
  discord: "chat",
  slack: "chat",
  telegram: "chat",
  whatsapp: "chat",
  circle: "membership",
  skool: "membership",
  "mighty-networks": "membership",
  substack: "media",
}

const FORMAT_LABELS = {
  feed: "Forum / feed-based",
  chat: "Chat communities",
  membership: "Private membership",
  media: "Media / newsletter",
  other: "Other platforms",
}

function buildPlatformSupplyDemand(communities, platformBreakdown) {
  if (!platformBreakdown.length) {
    return { available: false, items: [], totalClicks: 0 }
  }

  const clicksByPlatform = {}
  communities.forEach(community => {
    const slug = platformSlugs(community)[0] || "independent"
    const clicks = toTrafficNumber(community.redirects)
    if (!clicks) return
    clicksByPlatform[slug] = (clicksByPlatform[slug] || 0) + clicks
  })

  const totalClicks = Object.values(clicksByPlatform).reduce(
    (sum, count) => sum + count,
    0
  )
  if (!totalClicks) {
    return { available: false, items: [], totalClicks: 0 }
  }

  const items = platformBreakdown
    .map(platform => ({
      slug: platform.slug,
      name: platform.name,
      communityCount: platform.count,
      communityPercent: platform.percent,
      clicks: clicksByPlatform[platform.slug] || 0,
      clickPercent: percent(clicksByPlatform[platform.slug] || 0, totalClicks),
    }))
    .filter(item => item.communityCount > 0 && item.clicks > 0)
    .sort((a, b) => b.communityCount - a.communityCount)

  return {
    available: items.length >= 2,
    items,
    totalClicks,
  }
}

function buildFormatMix(communities, platformBreakdown) {
  if (!platformBreakdown.length) {
    return { available: false, items: [] }
  }

  const counts = {}
  let mappedCommunities = 0

  communities.forEach(community => {
    const slug = platformSlugs(community)[0] || "independent"
    const formatKey = PLATFORM_FORMAT_MAP[slug] || "other"
    counts[formatKey] = (counts[formatKey] || 0) + 1
    if (PLATFORM_FORMAT_MAP[slug]) mappedCommunities += 1
  })

  const mappedShare = percent(mappedCommunities, communities.length)
  const items = Object.keys(counts)
    .map(key => ({
      key,
      name: FORMAT_LABELS[key] || key,
      count: counts[key],
      percent: percent(counts[key], communities.length),
    }))
    .filter(item => item.count > 0)
    .sort((a, b) => b.count - a.count)

  return {
    available:
      items.length >= 2 &&
      mappedShare >= 50 &&
      items.filter(item => item.key !== "other").length >= 2,
    items,
  }
}

export { computeTopicStats, platformSlugs, percent }
